import { SkipNavContent } from "@reach/skip-nav";
import StickyNotification from "atoms/sticky-notification/sticky-notification";
import { LayoutGroup } from "framer-motion";
import BookviewContentWidthToggleButton from "molecules/bookview-content-expansion/bookview-content-width-toggle-button";
import ErrorBoundary from "molecules/errors/error-boundary";
import BookViewMetaNav from "molecules/metanavs/bookview-metanav";
import OfflineBookviewAnalytics from "molecules/offline-bookview-analytics/offline-bookview-analytics";
import BreadcrumbsMenu from "organisms/breadcrumbs/breadcrumbs-menu";
import React, { useEffect, useMemo, useRef } from "react";
import { BrowserUtils } from "utilities/browser-utils";
import useNetworkInformation from "utilities/contexts/network-information/use-network-information";
import useCurrentPublication from "utilities/contexts/use-current-publication";
import { useGlobalState } from "utilities/contexts/use-global-state-context";
import { Breakpoints } from "utilities/enumerations/breakpoints";
import useTias from "utilities/hooks/domain/publications/use-tias";
import useBreakpoint, {
    BreakpointComparer,
} from "utilities/hooks/use-breakpoint";
import { useLocalization } from "utilities/hooks/use-localization";
import useSectionAdjustment from "utilities/hooks/use-section-adjustment";
import { NestedRoutes } from "utilities/routing/nested-route";
import StringUtils from "utilities/string-utils";
import { useBookviewTabPanel } from "utilities/contexts/bookview-tab-panel/use-bookview-tab-panel";
import useBookviewSearchHighlights from "utilities/hooks/use-bookview-search-highlights";
import { useAtomValue } from "jotai";
import { BookviewSearchTermAtom } from "utilities/atoms/bookview/bookview-search-term-atom";
import { BookviewSearchResultsAtom } from "utilities/atoms/bookview/bookview-search-results-atom";
import { useMobileSecondaryNavContent } from "utilities/contexts/use-mobile-secondary-nav/use-mobile-secondary-nav-content";

const CSS_BASE_CLASS = "c-publication-page-layout";

const BookViewLayoutContent: React.FunctionComponent<any> = React.memo(
    (props: React.PropsWithChildren<any>) => {
        const { isOnline } = useNetworkInformation();
        const { t } = useLocalization();
        const { globalState } = useGlobalState();
        const { currentIdentity } = globalState;
        const { publication, loaded } = useCurrentPublication();
        const { loadTias } = useTias();
        const { isOpen, isBookviewSearchPanelOpen } = useBookviewTabPanel();
        const { isBookviewSearchPanelOpen: isMobileBookviewSearchPanelOpen } =
            useMobileSecondaryNavContent();
        const searchTerm = useAtomValue(BookviewSearchTermAtom);
        const searchResults = useAtomValue(BookviewSearchResultsAtom);
        const publicationContentRef = useRef<HTMLDivElement>(null);
        const isMobile = useBreakpoint(
            Breakpoints.Tablet,
            BreakpointComparer.MaxWidth
        );

        useEffect(() => {
            if (publication.id != null) loadTias(publication.id);
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [publication.id]);

        const openBookviewTabPanelSpacer =
            isOpen && !BrowserUtils.isIE() && isOnline;
        const renderUnpublishedSticky =
            currentIdentity?.isAdmin() &&
            loaded &&
            StringUtils.isEmpty(publication?.publishedOn);
        const classNames = `${CSS_BASE_CLASS} ${
            isOpen && isOnline ? "-reference-panel-open" : ""
        }`;

        const memoRoutes = useMemo(
            () => <NestedRoutes routes={props.routes} />,
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [globalState.currentIdentity?.userLogin?.id, props.routes]
        );

        // Ensures that section scrolls to top
        useSectionAdjustment();

        useBookviewSearchHighlights({
            contentRef: publicationContentRef,
            searchTerm,
            searchResults,
            enable:
                isBookviewSearchPanelOpen || isMobileBookviewSearchPanelOpen,
        });

        return (
            <div className={classNames}>
                <BookViewMetaNav />
                <ErrorBoundary>
                    <LayoutGroup>
                        <div className={`${CSS_BASE_CLASS}__content`}>
                            <BreadcrumbsMenu />
                            <SkipNavContent>
                                <OfflineBookviewAnalytics />
                                {BrowserUtils.isIE() ? (
                                    <div
                                        ref={publicationContentRef}
                                        className={`${CSS_BASE_CLASS}__content__main`}>
                                        <NestedRoutes routes={props.routes} />
                                    </div>
                                ) : (
                                    <div
                                        ref={publicationContentRef}
                                        className={`${CSS_BASE_CLASS}__content__main`}>
                                        {memoRoutes}
                                    </div>
                                )}
                            </SkipNavContent>
                            {!isMobile && (
                                <div
                                    className={`${CSS_BASE_CLASS}__content-width-toggle-button`}>
                                    <BookviewContentWidthToggleButton />
                                </div>
                            )}
                        </div>
                        <div
                            className={`bookview-tab-panel-spacer ${openBookviewTabPanelSpacer ? "-open" : ""}`}></div>
                    </LayoutGroup>
                </ErrorBoundary>
                {renderUnpublishedSticky && (
                    <StickyNotification>{t("unpublished")}</StickyNotification>
                )}
            </div>
        );
    }
);

export default BookViewLayoutContent;
